import { memo, useState } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Dialog } from 'antd-mobile';
import { Toast, TrackerCustomEvent, useTracker } from 'common-package';
import { Input } from 'antd';
import { useLocation } from 'react-router';
import service from '@/service';
import { useKeyboardAndroid } from '@/hooks';
import { TechniqueScore } from '..';
import cx from './index.module.scss';

const ORDER_SOURCE = ['美团', '大众点评', '抖音', '线下支付', '充值耗卡'];

const SupplyForm = ({
  cancelCallback,
  evaluateInfo,
  isAmountNecessary,
}: {
  cancelCallback: (needCloseDrawer?: boolean) => void;
  evaluateInfo: ServiceEvaluateInfoResponse;
  isAmountNecessary: boolean;
}) => {
  const keyboardVisible = useKeyboardAndroid();
  const [isKeyboardRemark, setKeyboardRemark] = useState<boolean>(false);
  const { state } = useLocation();
  const [amount, setAmount] = useState<string | number>(evaluateInfo?.amount);
  const [source, setSource] = useState(evaluateInfo?.source);
  const [robotTechScore, setRobotTechScore] = useState(evaluateInfo?.robotTechScore);
  const [accuracyScore, setAccuracyScore] = useState(evaluateInfo?.accuracyScore);
  const [amountWarnText, setAmountWarnText] = useState('');
  const [remark, setRemark] = useState<string>(evaluateInfo?.remark);
  const [submitLoading, setSubmitLoading] = useState(false);
  const { sendEvent } = useTracker();

  const handleAmountIsNecessary = (isAmountNecessary: boolean) => {
    if (isAmountNecessary) {
      return amount === '' || !amount || !source || !robotTechScore || !accuracyScore || amountWarnText !== '';
    }

    return !source || !robotTechScore || !accuracyScore || amountWarnText !== '';
  };

  return (
    <div className={cx('container')}>
      <div className={cx('title')}>请补充订单信息</div>

      <div
        className={cx(
          'content-box',
          { 'scroll-box': keyboardVisible },
          { 'not-scroll': isKeyboardRemark && keyboardVisible }
        )}
      >
        {!(isKeyboardRemark && keyboardVisible) && (
          <>
            <div className={cx('card', 'order-amount')}>
              <div className={cx('title', { disabled: !!evaluateInfo?.amount })}>
                订单金额
                {isAmountNecessary && <span className={cx('tag')}>*</span>}
                <span className={cx('warn-text')}>{amountWarnText}</span>
              </div>
              <Input
                allowClear={!evaluateInfo?.amount}
                disabled={!!evaluateInfo?.amount}
                prefix="￥"
                placeholder="0.00元"
                value={amount ?? ''}
                onChange={e => {
                  const inputValue = e.target.value;
                  const reg = /^\d*(\.\d{0,2})?$/;

                  const parsedValue = parseFloat(inputValue);
                  if ((!Number.isNaN(parsedValue) && reg.test(inputValue)) || inputValue === '') {
                    setAmount(inputValue);
                    parsedValue > 10000 ? setAmountWarnText('超过10000元金额范围') : setAmountWarnText('');
                  }
                }}
              />
            </div>

            <div className={cx('card')}>
              <div className={cx('title', { disabled: !!evaluateInfo?.source })}>
                订单来源
                <span className={cx('tag')}>*</span>
              </div>
              <div className={cx('source-content')}>
                {ORDER_SOURCE.map((item, index) => (
                  <div
                    key={item}
                    className={cx('source-block', { selected: source === item })}
                    onClick={() => {
                      !evaluateInfo?.source && setSource(item);
                    }}
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className={cx('card')}>
              <div className={cx('title')}>
                <span
                  className={cx({
                    'title-text-disabled': !!evaluateInfo?.accuracyScore && !!evaluateInfo?.robotTechScore,
                  })}
                >
                  机器人评分
                  <span className={cx('tag')}>*</span>
                </span>

                <span className={cx('tips')}>
                  <InfoCircleOutlined style={{ marginRight: 4 }} />
                  5颗星代表很满意 1颗星代表不满意
                </span>
              </div>
              <div className={cx('score-content')}>
                <TechniqueScore
                  robotTechScore={robotTechScore ?? 0}
                  accuracyScore={accuracyScore ?? 0}
                  onAccuracyScoreChange={score => {
                    // 数据不存在可以更新
                    !evaluateInfo?.accuracyScore && setAccuracyScore(score);
                  }}
                  onRobotTechScoreChange={score => {
                    // 数据不存在可以更新
                    !evaluateInfo?.robotTechScore && setRobotTechScore(score);
                  }}
                />
              </div>
            </div>
          </>
        )}

        <div className={cx('card', 'remark')}>
          <div className={cx('title')}>备注</div>
          <div className={cx('remark-box')}>
            <Input
              placeholder="可备注更多订单信息"
              value={remark ?? ''}
              onFocus={() => setKeyboardRemark(true)}
              onBlur={() => setKeyboardRemark(false)}
              onChange={e => {
                if (e.target.value?.length > 20) {
                  Toast.show('最多可填写20字符');
                } else {
                  setRemark(e.target.value);
                }
              }}
            />
          </div>
          <div className={cx('remark-show-count')}>{remark?.length || 0}/20</div>
        </div>
      </div>
      {!(isKeyboardRemark && keyboardVisible) && (
        <div className={cx('footer')}>
          <Button
            className={cx('cancel')}
            onClick={() => {
              Dialog.confirm({
                title: '确认退出评价吗？',
                content: '确认后将退出评价',
                confirmText: '确认',
                onConfirm: async () => {
                  cancelCallback(!isAmountNecessary);
                  // homePage页面展示不同
                  !isAmountNecessary &&
                    sendEvent(TrackerCustomEvent.ServiceRecordDrawerAlertSkip, {
                      et: 'CLK',
                    });
                },
              });
            }}
          >
            {/* // homePage页面展示不同 */}
            {!isAmountNecessary ? '跳过' : '取消'}
          </Button>

          <Button
            loading={submitLoading}
            disabled={handleAmountIsNecessary(isAmountNecessary)}
            className={cx('submit')}
            onClick={async () => {
              const params: ServiceEvaluateSubmitParams = {
                amount: amount ? Number(amount) : null,
                source,
                robotTechScore,
                accuracyScore,
                remark,
                serviceOrderId: evaluateInfo?.serviceOrderId ?? state.orderId,
              };
              setSubmitLoading(true);
              try {
                const res = await service.serviceRecordApi.updateServiceRecord(params);
                if (res?.data) {
                  cancelCallback(!isAmountNecessary);
                  sendEvent(TrackerCustomEvent.ServiceRecordDrawerAlertSubmit, {
                    et: 'CLK',
                  });

                  Toast.success('提交成功');
                } else {
                  Toast.fail('提交失败');
                }
              } catch (e) {
              } finally {
                setSubmitLoading(false);
              }
            }}
          >
            提交
          </Button>
        </div>
      )}
    </div>
  );
};

export default memo(SupplyForm);
